import React, { useState, useEffect } from 'react'
import styles from './BookingSection.module.css'
import { validateOtp, maskMobileNumber } from '@/utils/validation'

interface Step2OtpProps {
  name: string
  mobileNumber: string
  isLoading: boolean
  isResending: boolean
  errorMessage: string | null
  successMessage: string | null
  onVerifyAndBook: (otp: string, consentGiven: boolean) => void
  onResendOtp: () => void
  onEditDetails: () => void
  onClearError: () => void
}

export const Step2Otp: React.FC<Step2OtpProps> = ({
  name,
  mobileNumber,
  isLoading,
  isResending,
  errorMessage,
  successMessage,
  onVerifyAndBook,
  onResendOtp,
  onEditDetails,
  onClearError,
}) => {
  const [otp, setOtp] = useState('')
  const [consentGiven, setConsentGiven] = useState(true)
  const [otpError, setOtpError] = useState<string | null>(null)
  const [consentError, setConsentError] = useState<string | null>(null)
  const [countdown, setCountdown] = useState(30)

  // Countdown timer for Resend OTP
  useEffect(() => {
    if (countdown <= 0) return
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [countdown])

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6)
    setOtp(val)
    if (errorMessage) onClearError()
    if (otpError) {
      const check = validateOtp(val)
      if (check.isValid) setOtpError(null)
    }
  }

  const handleResend = () => {
    if (countdown > 0 || isResending || isLoading) return
    onResendOtp()
    setCountdown(30)
    setOtp('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const otpCheck = validateOtp(otp)
    if (!otpCheck.isValid) {
      setOtpError(otpCheck.error || 'Please enter a valid OTP')
      return
    }

    if (!consentGiven) {
      setConsentError('Please accept the consent terms to complete your appointment booking')
      return
    }

    setOtpError(null)
    setConsentError(null)
    onVerifyAndBook(otp.trim(), consentGiven)
  }

  return (
    <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
      <div className={styles.formHeader}>
        <div className={styles.stepBadge}>STEP 2 OF 2</div>
        <h2 className={styles.formTitle}>Verify & Confirm Booking</h2>
        <p className={styles.formSubtitle}>
          We sent a verification code to your registered mobile number.
        </p>
      </div>

      {/* Patient Summary Card with Edit button */}
      <div className={styles.patientSummary}>
        <div className={styles.patientSummaryContent}>
          <div className={styles.patientAvatar} aria-hidden="true">👤</div>
          <div>
            <p className={styles.patientSummaryName}>{name}</p>
            <p className={styles.patientSummaryMobile}>{maskMobileNumber(mobileNumber)}</p>
          </div>
        </div>
        <button
          type="button"
          className={styles.editButton}
          onClick={onEditDetails}
          disabled={isLoading}
          aria-label="Edit name or mobile number"
        >
          ✏️ Edit
        </button>
      </div>

      {/* Global Error Banner */}
      {errorMessage && (
        <div className={styles.errorAlert} role="alert">
          <div className={styles.errorAlertIcon} aria-hidden="true">⚠️</div>
          <div className={styles.errorAlertContent}>
            <p className={styles.errorAlertTitle}>Verification Unsuccessful</p>
            <p className={styles.errorAlertDesc}>{errorMessage}</p>
          </div>
          <button
            type="button"
            className={styles.errorAlertClose}
            onClick={onClearError}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Success Notification (e.g. OTP resent) */}
      {successMessage && (
        <div className={styles.successBanner} role="status">
          <span aria-hidden="true">✓</span> {successMessage}
        </div>
      )}

      {/* OTP Input Field */}
      <div className={styles.fieldGroup}>
        <div className={styles.inputWrapper}>
          <div className={styles.otpLabelRow}>
            <label htmlFor="otp-input" className={styles.label}>
              Enter Verification Code (OTP) <span className={styles.requiredAsterisk}>*</span>
            </label>
            {/* Resend OTP button */}
            <button
              type="button"
              className={styles.resendBtn}
              onClick={handleResend}
              disabled={countdown > 0 || isResending || isLoading}
            >
              {isResending ? (
                'Resending code...'
              ) : countdown > 0 ? (
                `Resend code in ${countdown}s`
              ) : (
                'Resend OTP'
              )}
            </button>
          </div>

          <div className={styles.inputContainer}>
            <span className={styles.inputIcon} aria-hidden="true">🔑</span>
            <input
              id="otp-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`${styles.input} ${styles.otpInput} ${otpError ? styles.inputError : ''}`}
              placeholder="Enter 4 or 6-digit code"
              value={otp}
              onChange={handleOtpChange}
              disabled={isLoading}
              maxLength={6}
              autoFocus
              required
              aria-invalid={!!otpError}
              aria-describedby={otpError ? 'otp-error' : undefined}
            />
          </div>
          {otpError && (
            <p id="otp-error" className={styles.fieldErrorMessage} role="alert">
              {otpError}
            </p>
          )}
        </div>

        {/* Professional Consent Checkbox */}
        <div className={styles.consentWrapper}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkboxInput}
              checked={consentGiven}
              onChange={(e) => {
                setConsentGiven(e.target.checked)
                if (consentError && e.target.checked) setConsentError(null)
              }}
              disabled={isLoading}
            />
            <span className={styles.checkboxCustom} aria-hidden="true" />
            <span className={styles.checkboxText}>
              I consent to <strong>Nextgen Health Care Services</strong> and partner diagnostic network
              processing my details for appointment coordination, clinical diagnostic reporting,
              and healthcare service updates in compliance with the{' '}
              <a href="#privacy" className={styles.inlineLink}>
                Privacy Policy
              </a>
              .
            </span>
          </label>
          {consentError && (
            <p className={styles.fieldErrorMessage} role="alert">
              {consentError}
            </p>
          )}
        </div>
      </div>

      {/* Submit Button: Professional Name */}
      <button
        type="submit"
        className={styles.primaryButton}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className={styles.spinner} aria-hidden="true" />
            <span>Confirming Your Appointment...</span>
          </>
        ) : (
          <>
            <span aria-hidden="true">✓</span>
            <span>Verify &amp; Confirm Appointment</span>
          </>
        )}
      </button>

      {/* Back to Edit Option */}
      <div className={styles.backWrapper}>
        <button
          type="button"
          className={styles.textButton}
          onClick={onEditDetails}
          disabled={isLoading}
        >
          ← Change Name or Mobile Number
        </button>
      </div>
    </form>
  )
}

